'use client';

import { useEffect, useRef, useState } from 'react';
import ReactMapGl, { LngLatBoundsLike, LngLatLike, Popup, PopupProps, MapRef, Marker } from 'react-map-gl/mapbox';
import useSupercluster from 'use-supercluster';
import Link from 'next/link';

import { normalizeObject } from 'src/services/content-utils';
import useMapState, { ViewState } from 'src/hooks/useMapState';
import { locationUrl } from 'src/services/route-utils';

import MapPin from '../../public/images/locations/map-pin.svg';
import '../styles/mapbox-gl.css';

type PopupItemProps = {
    name?: string;
    address?: string;
    city?: any;
    slug?: string;
    geo_point: LngLatLike;
};

type Props = {
    id: string;
    initialBounds?: LngLatBoundsLike | undefined;
    initialPopup?: PopupItemProps | undefined;
    highlightedLocation?: any;
    view?: ViewState;
};

export default function Map({
    id,
    highlightedLocation,
    view,
    initialBounds = undefined,
    initialPopup = undefined,
}: Props) {
    const { accessToken, bounds, locations, viewState, setBounds, setViewState, setRenderedLocations } = useMapState();
    const timer = useRef<NodeJS.Timeout>(undefined);
    const [popupProps, setPopupProps] = useState<PopupProps | null>(null);
    const [viewBounds] = useState(initialBounds || bounds);
    const mapRef = useRef<MapRef>(undefined);

    const mapBounds: any = mapRef.current?.getMap().getBounds()?.toArray().flat();
    const { clusters, supercluster } = useSupercluster({
        points:
            locations?.features.map((item) => ({ ...item, properties: { ...item.properties, cluster: false } })) || [],
        zoom: viewState?.zoom || 12,
        bounds: mapBounds,
        options: {
            radius: 75,
            maxZoom: 20,
        },
    });

    useEffect(() => {
        if (initialBounds) {
            setBounds(initialBounds);
        }
    }, [initialBounds, setBounds]);

    useEffect(() => {
        if (initialPopup) {
            setPopup(initialPopup);
        }
    }, [initialPopup]);

    useEffect(() => {
        if (view) {
            setViewState(view);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);

    const getRenderedLocations = () => {
        const itemFromGeoJsonFeature = (item) => ({
            ...item.properties,
            city: normalizeObject(item.properties.city),
            geo_json: normalizeObject(item.properties.geo_json),
        });

        return clusters.reduce((result, item) => {
            if (!item.properties.cluster) {
                return [...result, itemFromGeoJsonFeature(item)];
            }

            return [
                ...result,
                ...(supercluster?.getLeaves(item.properties.cluster_id, 100).map(itemFromGeoJsonFeature) || []),
            ];
        }, []);
    };

    const onMove = (event) => {
        setViewState(event.viewState);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            const newLocations = getRenderedLocations();
            setRenderedLocations(newLocations);
        }, 300);
    };

    const setPopup = (item: PopupItemProps) => {
        const popupOffsets = {
            bottom: [0, -50],
        };
        setPopupProps({
            longitude: item.geo_point[0],
            latitude: item.geo_point[1],
            offset: popupOffsets as any,
            children: (
                <div className="border-primary w-[240px] border-t-4 p-4">
                    <div className="font-display text-primary text-lg">{item?.name}</div>
                    <div className="mt-2 text-xs">{item?.address}</div>
                    <div className="text-xs">{item?.city?.name}</div>
                    <Link className="btn btn-primary mt-2 block" href={locationUrl(item)}>
                        Vezi locația
                    </Link>
                </div>
            ),
            onClose: () => {
                setPopupProps(null);
            },
        });
    };

    return (
        <ReactMapGl
            reuseMaps
            id={id}
            ref={mapRef as any}
            initialViewState={{ bounds: viewBounds }}
            {...viewState}
            onMove={onMove}
            minZoom={4.5}
            mapStyle="mapbox://styles/mapbox/streets-v10"
            mapboxAccessToken={accessToken}
            style={{ minHeight: 1 }}>
            {clusters.map((cluster) => {
                const [longitude, latitude] = cluster.geometry.coordinates;

                if (!cluster.properties.cluster) {
                    const isHighlighted = highlightedLocation?.id === cluster.properties.id;
                    const pin = (
                        <MapPin
                            className={`origin-bottom cursor-pointer md:scale-150 ${
                                isHighlighted ? 'text-primary' : 'text-third'
                            }`}
                            onClick={() => {
                                mapRef.current?.flyTo({
                                    center: [longitude, latitude],
                                    zoom: 17,
                                });

                                setTimeout(() => {
                                    setPopup({
                                        ...cluster.properties,
                                        geo_point: [longitude, latitude],
                                    });
                                }, 100);
                            }}
                        />
                    );

                    return (
                        <Marker
                            longitude={longitude}
                            latitude={latitude}
                            anchor="bottom"
                            key={`${cluster.properties.slug}`}>
                            {isHighlighted ? <div className="animate-bounce">{pin}</div> : pin}
                        </Marker>
                    );
                }

                const { point_count, cluster_id } = cluster.properties;
                const size = 45 + point_count;
                const isHighlighted =
                    highlightedLocation &&
                    supercluster?.getLeaves(cluster_id).find((item) => item.properties.id === highlightedLocation.id);

                return (
                    <Marker key={`cluster-${cluster_id}`} longitude={longitude} latitude={latitude}>
                        <div
                            className={`${
                                isHighlighted ? 'bg-primary' : 'bg-third'
                            } flex cursor-pointer items-center justify-center rounded-full border-2 border-white font-bold text-white md:scale-150`}
                            style={{
                                width: `${size}px`,
                                height: `${size}px`,
                            }}
                            onClick={() => {
                                const zoom = supercluster?.getClusterExpansionZoom(cluster_id);
                                if (zoom) {
                                    mapRef.current?.easeTo({
                                        center: [longitude, latitude],
                                        zoom,
                                    });
                                }
                            }}>
                            {point_count}
                        </div>
                    </Marker>
                );
            })}
            {popupProps && <Popup {...popupProps} focusAfterOpen={false} />}
        </ReactMapGl>
    );
}
